import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useThemeContext, api } from '../../App';
import AiAssistant from '../../components/AiAssistant';
import CommandPalette from '../../components/CommandPalette';
import InvestorTour from '../../components/InvestorTour';
import { useRBAC } from '../../hooks/useRBAC';
import KeyHealthBanner from './KeyHealthBanner';

const navGroups = [
  {
    label: 'Overview',
    items: [
      { path: '/', label: 'Dashboard', icon: 'fa-solid fa-chart-pie' },
      { path: '/analytics', label: 'Analytics', icon: 'fa-solid fa-chart-line' },
      { path: '/leaderboard', label: 'Leaderboard', icon: 'fa-solid fa-ranking-star' },
      { path: '/scoring', label: 'Lead Scoring', icon: 'fa-solid fa-brain' },
    ],
  },
  {
    label: 'Lead Management',
    items: [
      { path: '/leads', label: 'Lead Database', icon: 'fa-solid fa-address-book' },
      { path: '/scanner', label: 'Permit Scanner', icon: 'fa-solid fa-tower-broadcast' },
      { path: '/roof-age', label: 'Roof Age Trigger', icon: 'fa-solid fa-house-chimney-crack' },
      { path: '/map', label: 'Lead Map', icon: 'fa-solid fa-map-location-dot' },
      { path: '/alerts', label: 'Lead Alerts', icon: 'fa-solid fa-bell' },
      { path: '/mining', label: 'Lead Mining', icon: 'fa-solid fa-satellite-dish' },
      { path: '/enrichment-lab', label: 'Enrichment Lab', icon: 'fa-solid fa-flask' },
      { path: '/skip-trace', label: 'Skip Trace', icon: 'fa-solid fa-magnifying-glass-location' },
      { path: '/title', label: 'Title & Lien Check', icon: 'fa-solid fa-file-contract' },
      { path: '/contractors', label: 'Contractors', icon: 'fa-solid fa-helmet-safety' },
    ],
  },
  {
    label: 'Sales',
    items: [
      { path: '/closer', label: 'AI Close Copilot', icon: 'fa-solid fa-shield-halved' },
      { path: '/voice', label: 'Voice AI Closer', icon: 'fa-solid fa-phone-volume' },
      { path: '/turf', label: 'Turf Mode', icon: 'fa-solid fa-map-location-dot' },
      { path: '/proposals', label: 'Proposals', icon: 'fa-solid fa-file-invoice' },
      { path: '/credit', label: 'Credit Center', icon: 'fa-solid fa-shield-check' },
      { path: '/projects', label: 'Install Pipeline', icon: 'fa-solid fa-helmet-safety' },
      { path: '/templates', label: 'Templates', icon: 'fa-solid fa-palette' },
      { path: '/campaigns', label: 'Campaigns', icon: 'fa-solid fa-bullhorn' },
      { path: '/appointments', label: 'Appointments', icon: 'fa-solid fa-calendar-check' },
    ],
  },
  {
    label: 'Automation',
    items: [
      { path: '/autopilot', label: 'Auto-Pilot', icon: 'fa-solid fa-robot' },
      { path: '/push', label: 'Push Alerts', icon: 'fa-solid fa-mobile-screen' },
      { path: '/sdr', label: 'AI SDR Autopilot', icon: 'fa-solid fa-headset' },
      { path: '/webhooks', label: 'CRM Integrations', icon: 'fa-solid fa-plug', adminOnly: true },
    ],
  },
  {
    label: 'Field Ops',
    items: [
      { path: '/storms', label: 'Storm Center', icon: 'fa-solid fa-hurricane' },
      { path: '/rates', label: 'Utility Rate Increases', icon: 'fa-solid fa-arrow-trend-up' },
      { path: '/video', label: 'Video Enhance', icon: 'fa-solid fa-clapperboard' },
      { path: '/hiring', label: 'Hiring Center', icon: 'fa-solid fa-user-plus', managerOnly: true },
    ],
  },
  {
    label: 'Investor & Growth',
    items: [
      { path: '/prove-it', label: 'Prove It', icon: 'fa-solid fa-rocket', managerOnly: true },
    ],
  },
  {
    label: 'Account',
    items: [
      { path: '/credits', label: 'Credit Store', icon: 'fa-solid fa-coins' },
      { path: '/company', label: 'Company Profile', icon: 'fa-solid fa-building' },
      { path: '/referrals', label: 'Referrals', icon: 'fa-solid fa-gift' },
      { path: '/settings', label: 'Settings', icon: 'fa-solid fa-sliders' },
    ],
  },
];

const adminGroup = {
  label: 'Admin',
  items: [{ path: '/admin', label: 'Administration', icon: 'fa-solid fa-shield-halved' }],
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme, isDark } = useThemeContext();
  const { isAdmin, isManager, role } = useRBAC();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/api/notifications');
      setNotifications(res.data.notifications || []);
      const count = res.data.unread_count || 0;
      setUnreadCount(count);
      // Update browser tab title
      document.title = count > 0 ? `(${count}) Samaritan Solar` : 'Samaritan Solar';
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Keyboard shortcut: Cmd+K / Ctrl+K for command palette
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const markAllRead = async () => {
    try {
      await api.post('/api/notifications/read');
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      document.title = 'Samaritan Solar';
    } catch {}
  };

  const handleCmdAction = (action) => {
    if (action === 'toggle-theme') toggleTheme();
    if (action === 'create-lead') navigate('/leads');
    if (action === 'export-csv') {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      window.open(`${API_URL}/api/leads/export/csv`, '_blank');
    }
  };

  // Build nav: filter adminOnly items for non-admins, add Admin group for admins
  const allGroups = [
    ...navGroups.map(g => ({
      ...g,
      items: g.items.filter(item => (!item.adminOnly && !item.managerOnly) || isManager),
    })).filter(g => g.items.length > 0),
    ...(isManager ? [adminGroup] : []),
  ];

  const roleLabel = isAdmin ? 'Admin' : role === 'investor' ? 'Investor' : 'Sales Rep';
  const roleBadge = isAdmin ? 'bg-amber-100 text-amber-700' : role === 'investor' ? 'bg-indigo-100 text-indigo-700' : 'bg-primary/10 text-primary';

  return (
    <div className="flex h-screen bg-background overflow-hidden" data-testid="dashboard-layout">
      {mobileOpen && <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed lg:static z-50 h-full flex flex-col bg-surface border-r border-border transition-all duration-300 ${collapsed ? 'w-[72px]' : 'w-[260px]'} ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-testid="sidebar">
        <div className={`flex items-center h-[72px] px-5 border-b border-border ${collapsed ? 'justify-center' : 'gap-3'}`}>
          {collapsed ? <img src="/samaritan-icon.png" alt="Samaritan Solar" className="w-9 h-9 rounded-lg" /> : (
            <div>
              <img src="/samaritan-logo.png" alt="Samaritan Solar" className="h-7" />
              <span className="block text-[9px] font-bold uppercase tracking-[0.2em] mt-1" style={{ color: '#FAAB1C' }}>Operations Suite</span>
            </div>
          )}
        </div>

        <nav className="flex-1 py-4 px-3 space-y-5 overflow-y-auto">
          {allGroups.map((group) => (
            <div key={group.label}>
              {!collapsed && <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-widest text-text-muted">{group.label}</p>}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
                        isActive
                          ? 'bg-primary/10 text-primary shadow-sm'
                          : 'text-text-secondary hover:text-text-primary hover:bg-surface-alt'
                      } ${collapsed ? 'justify-center' : ''}`
                    }
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <i className={`${item.icon} w-5 text-center text-[15px] group-hover:scale-110 transition-transform`} />
                    {!collapsed && <span className="flex-1">{item.label}</span>}
                    {!collapsed && item.adminOnly && (
                      <span className="text-[9px] font-bold uppercase text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full tracking-wider">Admin</span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-border">
          {!collapsed && (
            <div className="flex items-center gap-3 px-3 py-2 mb-2 rounded-xl bg-surface-alt">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-emerald-500 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-text-primary truncate">{user?.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${roleBadge}`}>
                    {roleLabel}
                  </span>
                  <span className="text-[10px] text-text-muted">{user?.credits || 0} cr</span>
                </div>
              </div>
            </div>
          )}
          <button
            onClick={() => { logout(); navigate('/auth'); }}
            className={`flex items-center gap-3 w-full px-3 py-2 rounded-xl text-sm text-text-muted hover:text-destructive hover:bg-destructive-light transition-colors ${collapsed ? 'justify-center' : ''}`}
            data-testid="logout-btn"
          >
            <i className="fa-solid fa-right-from-bracket w-5 text-center" />
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-[72px] flex items-center justify-between px-6 bg-surface border-b border-border flex-shrink-0" data-testid="top-header">
          <div className="flex items-center gap-4">
            <button className="lg:hidden text-text-secondary hover:text-text-primary p-2 rounded-lg hover:bg-surface-alt transition-colors" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <i className="fa-solid fa-bars text-lg" />
            </button>
            <button className="hidden lg:flex items-center gap-2 text-text-muted hover:text-text-secondary p-2 rounded-lg hover:bg-surface-alt transition-colors" onClick={() => setCollapsed(!collapsed)} data-testid="collapse-sidebar-btn">
              <i className={`fa-solid ${collapsed ? 'fa-angles-right' : 'fa-angles-left'} text-sm`} />
            </button>

            {/* Search / Command Palette Trigger */}
            <button
              onClick={() => setCmdOpen(true)}
              className="hidden md:flex items-center gap-3 px-4 py-2 rounded-xl bg-surface-alt border border-border hover:border-primary/30 hover:bg-surface transition-all cursor-pointer min-w-[260px]"
              data-testid="cmd-trigger"
            >
              <i className="fa-solid fa-magnifying-glass text-text-muted text-xs" />
              <span className="text-sm text-text-muted flex-1 text-left">Search anything...</span>
              <kbd className="text-[10px] font-mono font-bold text-text-muted bg-surface border border-border px-1.5 py-0.5 rounded-md">Cmd+K</kbd>
            </button>
          </div>
          <div className="flex items-center gap-2">
            {/* Credits Badge */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-primary/5 border border-primary/10">
              <i className="fa-solid fa-bolt text-primary text-xs" />
              <span className="text-xs font-semibold text-primary tabular-nums">{user?.credits || 0} Credits</span>
            </div>

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-xl text-text-muted hover:text-text-primary hover:bg-surface-alt transition-all"
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              data-testid="theme-toggle"
            >
              <i className={`fa-solid ${isDark ? 'fa-sun' : 'fa-moon'} text-base`} />
            </button>

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen && unreadCount > 0) markAllRead(); }}
                className="relative p-2.5 rounded-xl text-text-muted hover:text-text-primary hover:bg-surface-alt transition-colors"
                data-testid="notifications-btn"
              >
                <i className="fa-solid fa-bell text-base" />
                {unreadCount > 0 && <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-destructive rounded-full border-2 border-surface text-white text-[9px] font-bold flex items-center justify-center">{unreadCount}</span>}
              </button>
              {notifOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                  <div className="absolute right-0 top-12 w-80 bg-surface rounded-2xl shadow-2xl border border-border z-50 overflow-hidden animate-slide-down" data-testid="notification-dropdown">
                    <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-surface-alt">
                      <span className="text-sm font-semibold text-text-primary">Notifications</span>
                      {notifications.length > 0 && <button onClick={markAllRead} className="text-[10px] text-primary font-semibold hover:underline">Mark all read</button>}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="py-10 text-center"><i className="fa-solid fa-bell-slash text-2xl text-text-muted opacity-20 mb-2 block" /><p className="text-xs text-text-muted">No notifications yet</p></div>
                      ) : notifications.slice(0, 10).map((n, i) => {
                        const icons = { purchase: 'fa-coins text-primary', enrichment: 'fa-brain text-purple-500', mining: 'fa-satellite-dish text-primary', autopilot: 'fa-robot text-secondary', import: 'fa-file-import text-accent', signature: 'fa-file-signature text-primary', referral: 'fa-gift text-secondary' };
                        return (
                          <div key={n.id || i} className={`px-4 py-3 border-b border-border/50 hover:bg-surface-alt/50 transition-colors ${!n.read ? 'bg-primary/5' : ''}`}>
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-lg bg-surface-alt flex items-center justify-center flex-shrink-0"><i className={`fa-solid ${icons[n.type] || 'fa-bell text-text-muted'} text-xs`} /></div>
                              <div className="min-w-0 flex-1"><p className="text-sm font-medium text-text-primary">{n.title}</p><p className="text-xs text-text-muted mt-0.5">{n.message}</p><p className="text-[10px] text-text-muted mt-1">{new Date(n.created_at).toLocaleString()}</p></div>
                              {!n.read && <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-surface-alt transition-colors"
                data-testid="profile-dropdown-btn"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-emerald-500 flex items-center justify-center text-white text-xs font-bold">
                  {user?.name?.charAt(0) || 'U'}
                </div>
              </button>
              {profileOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
                  <div className="absolute right-0 top-12 w-56 bg-surface rounded-2xl shadow-2xl border border-border z-50 overflow-hidden animate-slide-down">
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-sm font-semibold text-text-primary">{user?.name}</p>
                      <p className="text-xs text-text-muted">{user?.email}</p>
                      <span className={`mt-1 inline-flex text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${roleBadge}`}>
                        {isAdmin ? 'Administrator' : roleLabel}
                      </span>
                    </div>
                    <div className="py-1">
                      <button onClick={() => { setProfileOpen(false); navigate('/settings'); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:bg-surface-alt hover:text-text-primary transition-colors">
                        <i className="fa-solid fa-user-gear w-4 text-center text-xs" />Profile & Settings
                      </button>
                      <button onClick={() => { setProfileOpen(false); navigate('/credits'); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:bg-surface-alt hover:text-text-primary transition-colors">
                        <i className="fa-solid fa-coins w-4 text-center text-xs" />{user?.credits || 0} Credits
                      </button>
                      {isManager && (
                        <button onClick={() => { setProfileOpen(false); navigate('/admin'); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50 transition-colors" data-testid="admin-panel-link">
                          <i className="fa-solid fa-shield-halved w-4 text-center text-xs" />Admin Panel
                        </button>
                      )}
                      <button onClick={toggleTheme} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:bg-surface-alt hover:text-text-primary transition-colors">
                        <i className={`fa-solid ${isDark ? 'fa-sun' : 'fa-moon'} w-4 text-center text-xs`} />{isDark ? 'Light Mode' : 'Dark Mode'}
                      </button>
                    </div>
                    <div className="border-t border-border py-1">
                      <button onClick={() => { logout(); navigate('/auth'); setProfileOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-destructive hover:bg-destructive-light transition-colors">
                        <i className="fa-solid fa-right-from-bracket w-4 text-center text-xs" />Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* AI Assistant Button */}
            <button onClick={() => setAiOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-primary to-emerald-600 text-white text-xs font-bold shadow-glow-green hover:shadow-lg hover:brightness-110 active:scale-[0.98] transition-all" data-testid="ai-assistant-btn">
              <i className="fa-solid fa-brain" />
              <span className="hidden sm:inline">AI Assistant</span>
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8" data-testid="main-content">
          <KeyHealthBanner />
          <Outlet />
        </main>
      </div>

      <InvestorTour />

      {/* AI Assistant Panel */}
      <AiAssistant isOpen={aiOpen} onClose={() => setAiOpen(false)} />

      {/* Command Palette */}
      <CommandPalette isOpen={cmdOpen} onClose={() => setCmdOpen(false)} onAction={handleCmdAction} />

      {/* Floating AI Button (mobile) */}
      {!aiOpen && (
        <button
          onClick={() => setAiOpen(true)}
          className="fixed bottom-6 right-6 z-40 lg:hidden w-14 h-14 rounded-full bg-gradient-to-br from-primary to-emerald-600 text-white shadow-lg shadow-primary/30 flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
          data-testid="ai-fab-btn"
        >
          <i className="fa-solid fa-brain text-lg" />
        </button>
      )}
    </div>
  );
}
